(() => {
  const widgetSelector = '[data-collection-system-widget]';
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const rounded = (value, decimals = 1) => Number(value.toFixed(decimals));
  const wholeNumber = (value) => Math.round(value).toLocaleString('en-AU');

  const flowFromIntensity = (value, fullSpeedValue) => {
    const intensity = clamp(value / fullSpeedValue, 0, 1);
    return 2.5 - (intensity * 1.7);
  };

  const formatEnergy = (wattHours) => {
    if (wattHours >= 1000) return `${rounded(wattHours / 1000)} kWh`;
    return `${wholeNumber(wattHours)} Wh`;
  };

  const formatHours = (hours) => {
    if (hours < 100) {
      const displayedHours = rounded(hours);
      return `${displayedHours} ${displayedHours === 1 ? 'hour' : 'hours'}`;
    }
    return `${wholeNumber(hours)} hours`;
  };

  const formatPower = (watts) => {
    if (watts >= 1000) return `${rounded(watts / 1000)} kW`;
    return `${wholeNumber(watts)} W`;
  };

  // Typical absorption voltage is 1.2 × nominal (14.4V on a 12V bank); charge power and source current use it, not nominal.
  const chargingVoltage = (nominalVoltage) => nominalVoltage * 1.2;

  const capitalSolarHours = {
    sydney: { summer: 6.1, winter: 2.9, label: 'Sydney' },
    melbourne: { summer: 6.4, winter: 2.2, label: 'Melbourne' },
    brisbane: { summer: 6.7, winter: 3.9, label: 'Brisbane' },
    perth: { summer: 7.9, winter: 3.0, label: 'Perth' },
    adelaide: { summer: 7.2, winter: 2.5, label: 'Adelaide' },
    hobart: { summer: 5.9, winter: 1.8, label: 'Hobart' },
    darwin: { summer: 5.5, winter: 5.8, label: 'Darwin' },
    canberra: { summer: 6.7, winter: 2.7, label: 'Canberra' },
  };

  const calculators = {
    'solar-panels': (values) => {
      const location = capitalSolarHours[values.location];
      const peakSunHours = location[values.season];
      const systemAllowance = 0.80;
      const dailyYield = values.panelPower * peakSunHours * systemAllowance;
      const dailyAmpHours = dailyYield / values.bankVoltage;
      let state = 'good';
      let status = 'Practical charge';

      if (dailyYield < 500) {
        state = 'caution';
        status = 'Low daily charge';
      } else if (dailyYield < 1500) {
        state = 'neutral';
        status = 'Light daily charge';
      } else if (dailyYield >= 3000) {
        status = 'High daily charge';
      }

      return {
        primary: `${rounded(dailyAmpHours)} Ah/day`,
        status,
        message: `${location.label} ${values.season} uses ${peakSunHours} peak-sun hours and an 80% system allowance: about ${formatEnergy(dailyYield)}/day into a ${wholeNumber(values.bankVoltage)}V bank.`,
        state,
        duration: flowFromIntensity(dailyYield, 3000),
      };
    },

    'solar-controllers': (values) => {
      // Panels rarely deliver their rated watts; MPPT controllers that allow over-paneling clip the rest.
      const realWorldPanelFactor = 0.9;
      const estimatedCurrent = rounded((values.arrayPower * realWorldPanelFactor) / values.bankVoltage);
      const utilisation = estimatedCurrent / values.controllerRating;
      const utilisationPercent = Math.round(utilisation * 100);
      let state = 'good';
      let status = 'Good fit';
      let message = `About ${utilisationPercent}% of the controller rating, allowing about 10% for real-world panel losses.`;

      if (utilisation > 1) {
        state = 'danger';
        status = 'Undersized';
        message = `The estimate exceeds the selected controller rating by about ${Math.max(1, Math.round((utilisation - 1) * 100))}%.`;
      } else if (utilisation > 0.8) {
        state = 'caution';
        status = 'Near limit';
      } else if (utilisation < 0.45) {
        state = 'neutral';
        status = 'Large headroom';
      }

      return {
        primary: `${rounded(estimatedCurrent)} A`,
        status,
        message,
        state,
        duration: flowFromIntensity(estimatedCurrent, values.controllerRating),
      };
    },

    batteries: (values) => {
      const usableCapacity = values.capacity * values.usableFactor;
      const usableEnergy = usableCapacity * values.bankVoltage;
      const runtime = usableCapacity / values.averageLoad;
      let state = 'good';
      let status = 'All-day estimate';
      let message = `Based on ${rounded(usableCapacity)}Ah usable (${formatEnergy(usableEnergy)} at ${wholeNumber(values.bankVoltage)}V) supplying a ${rounded(values.averageLoad)}A average battery-side load.`;

      if (runtime < 2) {
        state = 'caution';
        status = 'Short runtime';
      } else if (runtime < 6) {
        state = 'neutral';
        status = 'Moderate runtime';
      } else if (runtime > 12) {
        status = 'Extended runtime';
      }

      return {
        primary: formatHours(runtime),
        status,
        message,
        state,
        duration: flowFromIntensity(values.averageLoad, 100),
        derived: { usableFactor: `${Math.round(values.usableFactor * 100)}%` },
      };
    },

    inverters: (values) => {
      const minimumContinuous = Math.ceil((values.runningLoad * (1 + values.designMargin)) / 100) * 100;
      const minimumSurge = Math.ceil(Math.max(values.startupLoad, values.runningLoad) / 100) * 100;
      let status = 'Medium system';

      if (minimumContinuous < 500) status = 'Light loads';
      if (minimumContinuous >= 1500) status = 'High output';
      if (minimumContinuous >= 3000) status = 'Large system';

      return {
        primary: `${wholeNumber(minimumContinuous)} W`,
        status,
        message: `Also allow at least ${wholeNumber(minimumSurge)}W of short-duration surge capacity.`,
        state: 'good',
        duration: flowFromIntensity(values.runningLoad, 2500),
      };
    },

    converters: (values) => {
      const assumedEfficiency = 0.90;
      const inputCurrent = (values.outputVoltage * values.outputCurrent) / (values.inputVoltage * assumedEfficiency);
      let status = 'Stabilised';
      let state = 'neutral';

      if (values.outputVoltage > values.inputVoltage) {
        status = 'Step-up';
        state = 'good';
      } else if (values.outputVoltage < values.inputVoltage) {
        status = 'Step-down';
        state = 'good';
      }

      return {
        primary: `${rounded(inputCurrent)} A`,
        status,
        message: `A ${wholeNumber(values.inputVoltage)}V source supplies a ${wholeNumber(values.outputVoltage)}V, ${rounded(values.outputCurrent)}A load at an assumed 90% efficiency.`,
        state,
        duration: flowFromIntensity(values.outputVoltage * values.outputCurrent, 1000),
      };
    },

    'dc-dc-chargers': (values) => {
      const [sourceVoltage, outputVoltage] = values.voltageRoute.split(':').map(Number);
      const assumedConversionEfficiency = 0.90;
      const chargeTime = values.capacityToReplace / (values.chargerCurrent * values.chargeEfficiency);
      const sourceCurrent = (chargingVoltage(outputVoltage) * values.chargerCurrent) / (sourceVoltage * assumedConversionEfficiency);
      let state = 'good';
      let status = 'Ideal';

      if (chargeTime < 3) {
        state = 'danger';
        status = 'Too fast';
      } else if (chargeTime < 4) {
        state = 'caution';
        status = 'Fast charge';
      } else if (chargeTime <= 6) {
        state = 'good';
        status = 'Ideal';
      } else if (chargeTime <= 8) {
        state = 'neutral';
        status = 'Slow charging';
      } else {
        state = 'neutral';
        status = 'Trickle charging';
      }

      return {
        primary: formatHours(chargeTime),
        status,
        message: `A ${rounded(values.chargerCurrent)}A charger on a ${wholeNumber(sourceVoltage)}V → ${wholeNumber(outputVoltage)}V route may require about ${rounded(sourceCurrent)}A from the source when charging at ${rounded(chargingVoltage(outputVoltage))}V with an assumed 90% conversion efficiency.`,
        state,
        duration: flowFromIntensity(values.chargerCurrent, 50),
        derived: { chargeEfficiency: `${Math.round(values.chargeEfficiency * 100)}%` },
      };
    },

    'alternator-charging': (values) => {
      const chargeTime = values.capacityToReplace / (values.chargeCurrent * values.chargeEfficiency);
      const outputPower = chargingVoltage(values.bankVoltage) * values.chargeCurrent;
      let state = 'good';
      let status = 'Strong charge';

      // Status reflects recharge time only; the bank size isn't known, so charge-rate limits stay in the standing guidance.
      if (chargeTime < 2) {
        status = 'Fast charge';
      } else if (chargeTime > 8) {
        state = 'neutral';
        status = 'Slow charge';
      } else if (chargeTime > 5) {
        state = 'neutral';
        status = 'Moderate charge';
      }

      return {
        primary: formatHours(chargeTime),
        status,
        message: `${rounded(values.chargeCurrent)}A into a ${wholeNumber(values.bankVoltage)}V bank charging at ${rounded(chargingVoltage(values.bankVoltage))}V is about ${formatPower(outputPower)} of continuous alternator output. Set the regulator's current limit within the alternator's hot rating and the battery and BMS maximum charge current.`,
        state,
        duration: flowFromIntensity(values.chargeCurrent, 200),
        derived: { chargeEfficiency: `${Math.round(values.chargeEfficiency * 100)}%` },
      };
    },

    monitoring: (values) => {
      const assumedInverterEfficiency = 0.90;
      const inverterCurrent = values.inverterLoad / (values.bankVoltage * assumedInverterEfficiency);
      const dischargeCurrent = inverterCurrent + values.otherDcLoad;
      const maximumPathCurrent = Math.max(dischargeCurrent, values.chargingCurrent);
      const selectionCurrent = maximumPathCurrent * 1.25;
      let shuntRange = 300;
      let state = 'good';
      let status = 'Typical system';

      if (selectionCurrent > 300 && selectionCurrent <= 500) {
        shuntRange = 500;
        state = 'neutral';
        status = 'High current';
      } else if (selectionCurrent > 500 && selectionCurrent <= 1000) {
        shuntRange = 1000;
        state = 'caution';
        status = 'Large system';
      } else if (selectionCurrent > 1000 && selectionCurrent <= 2000) {
        shuntRange = 2000;
        state = 'caution';
        status = 'Very high current';
      } else if (selectionCurrent > 2000) {
        shuntRange = null;
        state = 'danger';
        status = 'Specialist design';
      }

      return {
        primary: shuntRange ? `${wholeNumber(shuntRange)} A shunt` : 'Above 2,000 A',
        status,
        message: `Estimated discharge current is about ${rounded(dischargeCurrent)}A versus ${rounded(values.chargingCurrent)}A charging. A 25% selection allowance gives ${rounded(selectionCurrent)}A before choosing a suitable product range.`,
        state,
        duration: flowFromIntensity(maximumPathCurrent, 500),
      };
    },

    applications: (values) => {
      const dailyAmpHours = values.averageLoad * values.dailyHours;
      const dailyWattHours = dailyAmpHours * values.bankVoltage;
      const oneDayNameplateCapacity = dailyAmpHours / values.usableFactor;
      let state = 'good';
      let status = 'Moderate use';

      if (dailyWattHours < 500) {
        state = 'neutral';
        status = 'Light use';
      } else if (dailyWattHours >= 1500 && dailyWattHours < 3000) {
        state = 'caution';
        status = 'High use';
      } else if (dailyWattHours >= 3000) {
        state = 'caution';
        status = 'Large daily load';
      }

      return {
        primary: `${rounded(dailyAmpHours)} Ah/day`,
        status,
        message: `About ${formatEnergy(dailyWattHours)}/day from a ${wholeNumber(values.bankVoltage)}V bank. One day represents about ${rounded(oneDayNameplateCapacity)}Ah of ${values.usableFactor > 0.5 ? 'lithium' : 'AGM/gel'} nameplate capacity at a ${Math.round(values.usableFactor * 100)}% usable allowance.`,
        state,
        duration: flowFromIntensity(dailyWattHours, 3000),
        derived: { applicationUsableFactor: `${Math.round(values.usableFactor * 100)}%` },
      };
    },
  };

  const initialiseCableVoltageDropWidget = (widget, inputs, primaryOutput, statusOutput, messageOutput) => {
    const endpoint = widget.dataset.widgetEndpoint;
    const submitButton = widget.querySelector('[data-widget-submit]');
    const result = widget.querySelector('.collection-system-widget__result');
    if (!endpoint || !submitButton || !result) return;

    const defaultButtonLabel = submitButton.textContent;
    let activeController = null;

    const setLoading = (loading) => {
      submitButton.disabled = loading;
      submitButton.textContent = loading ? 'Calculating…' : defaultButtonLabel;
      result.setAttribute('aria-busy', String(loading));
    };

    const markInputsChanged = () => {
      if (activeController) {
        activeController.abort();
        activeController = null;
      }
      setLoading(false);
      widget.dataset.widgetState = 'neutral';
      primaryOutput.textContent = 'Ready';
      statusOutput.textContent = 'Inputs changed';
      messageOutput.textContent = 'Recalculate to check the updated circuit against the approved cable range.';
    };

    const calculate = async () => {
      const values = {};
      let isValid = true;

      inputs.forEach((input) => {
        const value = Number.parseFloat(input.value);
        const passesNativeConstraints = typeof input.checkValidity !== 'function' || input.checkValidity();
        const inputIsValid = Number.isFinite(value) && value > 0 && passesNativeConstraints;
        input.setAttribute('aria-invalid', String(!inputIsValid));
        if (!inputIsValid) isValid = false;
        values[input.dataset.widgetInput] = value;
      });

      if (!isValid) {
        widget.dataset.widgetState = 'danger';
        primaryOutput.textContent = '—';
        statusOutput.textContent = 'Check values';
        messageOutput.textContent = 'Enter a valid voltage, current and one-way length before recalculating.';
        return;
      }

      if (activeController) activeController.abort();
      const requestController = new AbortController();
      activeController = requestController;
      setLoading(true);
      widget.dataset.widgetState = 'neutral';
      primaryOutput.textContent = 'Calculating…';
      statusOutput.textContent = 'Live check';
      messageOutput.textContent = 'Checking the approved cable range and voltage drop.';

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            inputs: {
              batteryVoltage: values.circuitVoltage,
              inverterCableDistance: values.oneWayLength,
              inverterWattage: values.circuitVoltage * values.circuitCurrent,
            },
          }),
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`Cable estimate returned ${response.status}`);

        const payload = await response.json();
        const cable = payload?.estimate?.circuits?.inverter?.cable;
        if (
          !cable ||
          typeof cable.sizeLabel !== 'string' ||
          !Number.isFinite(cable.voltageDropPercent) ||
          !Number.isFinite(cable.voltageDropVolts) ||
          !Number.isFinite(cable.minCurrentAmps)
        ) {
          throw new Error('Cable estimate response was incomplete');
        }
        if (activeController !== requestController) return;

        widget.dataset.widgetState = cable.voltageDropPercent <= 3 ? 'good' : 'caution';
        widget.style.setProperty('--flow-duration', `${flowFromIntensity(values.circuitCurrent, 300).toFixed(2)}s`);
        primaryOutput.textContent = cable.sizeLabel;
        statusOutput.textContent = `${rounded(cable.voltageDropPercent, 2)}% drop`;
        messageOutput.textContent = `${rounded(cable.voltageDropVolts, 2)}V drop over ${rounded(values.oneWayLength)}m one-way at ${rounded(values.circuitCurrent)}A. Stocked-cable rating: ${rounded(cable.minCurrentAmps)}A under the default installation assumptions.`;
      } catch (error) {
        if (error?.name === 'AbortError' || activeController !== requestController) return;
        widget.dataset.widgetState = 'danger';
        primaryOutput.textContent = 'Unavailable';
        statusOutput.textContent = 'Try again';
        messageOutput.textContent = 'The live cable check could not be completed. Try again or open the full cable planner.';
      } finally {
        if (activeController === requestController) {
          activeController = null;
          setLoading(false);
        }
      }
    };

    inputs.forEach((input) => {
      input.addEventListener(input.tagName === 'SELECT' ? 'change' : 'input', markInputsChanged);
    });
    submitButton.addEventListener('click', calculate);
    widget.dataset.systemWidgetInitialised = 'true';
    calculate();
  };

  const initialiseWidget = (widget) => {
    if (widget.dataset.systemWidgetInitialised === 'true') return;

    const calculator = calculators[widget.dataset.widgetType];
    const inputs = [...widget.querySelectorAll('[data-widget-input]')];
    const primaryOutput = widget.querySelector('[data-widget-primary]');
    const statusOutput = widget.querySelector('[data-widget-status]');
    const messageOutput = widget.querySelector('[data-widget-message]');

    if (
      widget.dataset.widgetType === 'cable-voltage-drop' &&
      inputs.length &&
      primaryOutput &&
      statusOutput &&
      messageOutput
    ) {
      initialiseCableVoltageDropWidget(widget, inputs, primaryOutput, statusOutput, messageOutput);
      return;
    }

    if (!calculator || !inputs.length || !primaryOutput || !statusOutput || !messageOutput) return;

    const update = () => {
      const values = {};
      let isValid = true;

      inputs.forEach((input) => {
        const usesTextValue = input.dataset.widgetValueType === 'text';
        const allowsZero = input.dataset.widgetAllowZero === 'true';
        const value = usesTextValue ? input.value : Number.parseFloat(input.value);
        const passesNativeConstraints = typeof input.checkValidity !== 'function' || input.checkValidity();
        const inputIsValid = usesTextValue
          ? value.trim().length > 0 && passesNativeConstraints
          : Number.isFinite(value) && (allowsZero ? value >= 0 : value > 0) && passesNativeConstraints;
        input.setAttribute('aria-invalid', String(!inputIsValid));
        if (!inputIsValid) isValid = false;
        values[input.dataset.widgetInput] = value;
      });

      if (!isValid) {
        widget.dataset.widgetState = 'danger';
        widget.style.setProperty('--flow-duration', '2.5s');
        primaryOutput.textContent = '—';
        statusOutput.textContent = 'Check values';
        messageOutput.textContent = 'Enter valid values in every field to update this estimate.';
        return;
      }

      const result = calculator(values);
      widget.dataset.widgetState = result.state;
      widget.style.setProperty('--flow-duration', `${result.duration.toFixed(2)}s`);
      primaryOutput.textContent = result.primary;
      statusOutput.textContent = result.status;
      messageOutput.textContent = result.message;

      Object.entries(result.derived || {}).forEach(([key, value]) => {
        const output = widget.querySelector(`[data-widget-derived="${key}"]`);
        if (output) output.textContent = value;
      });
    };

    inputs.forEach((input) => {
      input.addEventListener(input.tagName === 'SELECT' ? 'change' : 'input', update);
    });

    update();
    widget.dataset.systemWidgetInitialised = 'true';
  };

  const initialiseWidgets = (scope = document) => {
    if (scope.matches?.(widgetSelector)) initialiseWidget(scope);
    scope.querySelectorAll?.(widgetSelector).forEach(initialiseWidget);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseWidgets(), { once: true });
  } else {
    initialiseWidgets();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseWidgets(event.target));
})();
